import { type ReactNode, useCallback, useEffect, useState } from 'react';

import {
  dismissProfileProposal,
  fetchProfileProposal,
  type BrandBookProfileView,
  type CastGender,
  type ContextItem,
  fetchBrandBookProfile,
  fetchContextItems,
  isDutchLanguage,
  type ContentAddress,
  type ProductProfile,
  type ProductProfileField,
  type ProfileApprovedLine,
  type ProfileAutoSave,
  type ProfileProposal,
  type ProfileRemovedLine,
  type ProposalField,
  saveBrandBookProfile,
  type TtsProvider,
  undoProfileAutoSave,
} from './api.ts';
import { describeError, redirectIfUnauthorised } from './data.tsx';
import { Icon } from './Icon.tsx';
import { brandBookPath, Link } from './router.tsx';
import { localTime } from './schedule.ts';
import { useToast } from './toast.tsx';
import { Badge, Notice, Panel, Segmented, Skeleton } from './ui.tsx';

/**
 * The product profile (synthesis C7): what is true of a product on every
 * content — its language and form of address, where an ad sends the viewer,
 * who buys it and what they do today, the objections and the proof, the words
 * never to use, how its words are said, its voice, and the lines the operator
 * approved before.
 *
 * Two surfaces share this file. The **editor** on the brand book page is the
 * profile itself, every field, saved whole the way the `PUT` means it. The
 * **proposal** on the content page is what one planning conversation learned,
 * set beside what the product already knows; the server saves it to the brand
 * book when planning ends, and the panel offers **Undo** — nobody ticks boxes
 * to accept what the planner suggested (US-014).
 */

/** How each field is named on the page, in the profile's own order. */
const FIELD_LABELS: Record<ProductProfileField, string> = {
  language: 'Language',
  address: 'Form of address',
  website: 'Website',
  descriptor: 'Descriptor',
  cta: 'The ask',
  buyer: 'Buyer',
  statusQuo: 'What they do today instead',
  buyerWords: 'The buyer’s own words',
  competitors: 'Competitors, in their own words',
  objections: 'Objections',
  proof: 'Proof',
  noGos: 'Never say or show',
  raiseToFail: 'Banned rules raised to fail',
  allowLines: 'Fixed brand lines',
  pronunciations: 'Pronunciations',
  voice: 'Brand voice',
  cast: 'Voice cast',
  rejectedVoices: 'Rejected voices',
  approvedLines: 'Approved lines',
  removedApprovedLines: 'Removed by migration',
  rejectedLines: 'Rejected lines',
  notes: 'Notes',
};

/** The languages the server offers a content; a profile may name any tag. */
const LANGUAGE_SUGGESTIONS = ['nl-NL', 'nl-BE', 'en-US', 'en-GB', 'de-DE', 'fr-FR', 'es-ES', 'it-IT'];

/* ------------------------------------------------------------------ editor */

interface ObjectionRow {
  objection: string;
  answer: string;
}
interface ProofRow {
  claim: string;
  source: string;
  scope: string;
  relatedParty: boolean;
  permission: string;
  guarantee: boolean;
}
/** One claim of a competitor: the form lists claims, and rows with the same name are one competitor. */
interface CompetitorRow {
  name: string;
  text: string;
  url: string;
  fetched: string;
}
interface PronunciationRow {
  display: string;
  spoken: string;
  note: string;
}
interface CastRow {
  character: string;
  gender: CastGender | '';
  reference: string;
  look: string;
  provider: TtsProvider | '';
  voiceId: string;
  model: string;
  name: string;
  language: string;
  accent: string;
}
interface RejectedVoiceRow {
  voiceId: string;
  reason: string;
}

/** The whole profile as the form holds it: text everywhere, lists as rows. */
interface ProfileDraft {
  language: string;
  address: ContentAddress | '';
  website: string;
  ctaLabel: string;
  ctaUrl: string;
  buyer: string;
  statusQuo: string;
  descriptor: string;
  buyerWords: string;
  competitors: CompetitorRow[];
  objections: ObjectionRow[];
  proof: ProofRow[];
  noGos: string;
  raiseToFail: string;
  allowLines: string;
  pronunciations: PronunciationRow[];
  voiceProvider: TtsProvider | '';
  voiceId: string;
  voiceModel: string;
  voiceName: string;
  cast: CastRow[];
  rejectedVoices: RejectedVoiceRow[];
  /** The stored approved lines, kept whole so each keeps where it came from. */
  approvedLines: (string | ProfileApprovedLine)[];
  /** Lines typed here, one a line: saved as the operator's. */
  newApprovedLines: string;
  removedApprovedLines: ProfileRemovedLine[];
  rejectedLines: string;
  notes: string;
}

function toDraft(profile: ProductProfile): ProfileDraft {
  return {
    language: profile.language ?? '',
    address: profile.address ?? '',
    website: profile.website ?? '',
    ctaLabel: profile.cta?.label ?? '',
    ctaUrl: profile.cta?.url ?? '',
    buyer: profile.buyer ?? '',
    statusQuo: profile.statusQuo ?? '',
    descriptor: profile.descriptor ?? '',
    buyerWords: (profile.buyerWords ?? []).join('\n'),
    competitors: (profile.competitors ?? []).flatMap((one) =>
      one.claims.length === 0
        ? [{ name: one.name, text: '', url: '', fetched: '' }]
        : one.claims.map((claim) => ({ name: one.name, text: claim.text, url: claim.url, fetched: claim.fetched ?? '' })),
    ),
    objections: (profile.objections ?? []).map((one) => ({ objection: one.objection, answer: one.answer ?? '' })),
    proof: (profile.proof ?? []).map((one) => ({
      claim: one.claim,
      source: one.source,
      scope: one.scope ?? '',
      relatedParty: one.relatedParty === true,
      permission: one.permission ?? '',
      guarantee: one.guarantee === true,
    })),
    noGos: (profile.noGos ?? []).join('\n'),
    raiseToFail: (profile.raiseToFail ?? []).join('\n'),
    allowLines: (profile.allowLines ?? []).join('\n'),
    pronunciations: (profile.pronunciations ?? []).map((one) => ({
      display: one.display,
      spoken: one.spoken,
      note: one.note ?? '',
    })),
    voiceProvider: profile.voice?.provider ?? '',
    voiceId: profile.voice?.voiceId ?? '',
    voiceModel: profile.voice?.model ?? '',
    voiceName: profile.voice?.name ?? '',
    cast: (profile.cast ?? []).map((one) => ({
      character: one.character,
      gender: one.gender ?? '',
      reference: one.reference ?? '',
      look: one.look ?? '',
      provider: one.provider ?? '',
      voiceId: one.voiceId ?? '',
      model: one.model ?? '',
      name: one.name ?? '',
      language: one.language ?? '',
      accent: one.accent ?? '',
    })),
    rejectedVoices: (profile.rejectedVoices ?? []).map((one) => ({ voiceId: one.voiceId, reason: one.reason ?? '' })),
    approvedLines: [...(profile.approvedLines ?? [])],
    newApprovedLines: '',
    removedApprovedLines: [...(profile.removedApprovedLines ?? [])],
    rejectedLines: (profile.rejectedLines ?? []).join('\n'),
    notes: profile.notes ?? '',
  };
}

/**
 * The draft back into a profile. Blank text and empty rows are left out
 * rather than posted, which is what the server would make of them anyway —
 * and a half-filled row is posted as it is, so the server's own sentence says
 * what is missing from it.
 */
function fromDraft(draft: ProfileDraft): ProductProfile {
  const text = (value: string): string | undefined => (value.trim() === '' ? undefined : value.trim());
  const lines = (value: string): string[] | undefined => {
    const out = value
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line !== '');
    return out.length === 0 ? undefined : out;
  };
  const language = text(draft.language);
  const profile: ProductProfile = {};
  if (language !== undefined) profile.language = language;
  if (draft.address !== '' && (language === undefined || isDutchLanguage(language))) {
    profile.address = draft.address;
  }
  const website = text(draft.website);
  if (website !== undefined) profile.website = website;
  const ctaLabel = text(draft.ctaLabel);
  const ctaUrl = text(draft.ctaUrl);
  if (ctaLabel !== undefined || ctaUrl !== undefined) {
    profile.cta = {
      ...(ctaLabel === undefined ? {} : { label: ctaLabel }),
      ...(ctaUrl === undefined ? {} : { url: ctaUrl }),
    };
  }
  const buyer = text(draft.buyer);
  if (buyer !== undefined) profile.buyer = buyer;
  const statusQuo = text(draft.statusQuo);
  if (statusQuo !== undefined) profile.statusQuo = statusQuo;
  const descriptor = text(draft.descriptor);
  if (descriptor !== undefined) profile.descriptor = descriptor;
  const buyerWords = lines(draft.buyerWords);
  if (buyerWords !== undefined) profile.buyerWords = buyerWords;
  const competitors: NonNullable<ProductProfile['competitors']> = [];
  for (const row of draft.competitors) {
    const name = row.name.trim();
    const claimText = row.text.trim();
    const url = row.url.trim();
    const fetched = row.fetched.trim();
    if (name === '' && claimText === '' && url === '') continue;
    let competitor = competitors.find((one) => one.name === name);
    if (competitor === undefined) {
      competitor = { name, claims: [] };
      competitors.push(competitor);
    }
    if (claimText !== '' || url !== '') {
      competitor.claims.push({ text: claimText, url, ...(fetched === '' ? {} : { fetched }) });
    }
  }
  if (competitors.length > 0) profile.competitors = competitors;
  const objections = draft.objections
    .filter((row) => row.objection.trim() !== '' || row.answer.trim() !== '')
    .map((row) => ({
      objection: row.objection.trim(),
      ...(row.answer.trim() === '' ? {} : { answer: row.answer.trim() }),
    }));
  if (objections.length > 0) profile.objections = objections;
  const proof = draft.proof
    .filter((row) => row.claim.trim() !== '' || row.source.trim() !== '')
    .map((row) => ({
      claim: row.claim.trim(),
      source: row.source.trim(),
      ...(row.scope.trim() === '' ? {} : { scope: row.scope.trim() }),
      ...(row.relatedParty ? { relatedParty: true } : {}),
      ...(row.permission.trim() === '' ? {} : { permission: row.permission.trim() }),
      ...(row.guarantee ? { guarantee: true } : {}),
    }));
  if (proof.length > 0) profile.proof = proof;
  const noGos = lines(draft.noGos);
  if (noGos !== undefined) profile.noGos = noGos;
  const raiseToFail = lines(draft.raiseToFail);
  if (raiseToFail !== undefined) profile.raiseToFail = raiseToFail;
  const allowLines = lines(draft.allowLines);
  if (allowLines !== undefined) profile.allowLines = allowLines;
  const pronunciations = draft.pronunciations
    .filter((row) => row.display.trim() !== '' || row.spoken.trim() !== '')
    .map((row) => ({
      display: row.display.trim(),
      spoken: row.spoken.trim(),
      ...(row.note.trim() === '' ? {} : { note: row.note.trim() }),
    }));
  if (pronunciations.length > 0) profile.pronunciations = pronunciations;
  const voiceId = text(draft.voiceId);
  const voiceModel = text(draft.voiceModel);
  const voiceName = text(draft.voiceName);
  if (draft.voiceProvider !== '' || voiceId !== undefined || voiceModel !== undefined || voiceName !== undefined) {
    profile.voice = {
      ...(draft.voiceProvider === '' ? {} : { provider: draft.voiceProvider }),
      ...(voiceId === undefined ? {} : { voiceId }),
      ...(voiceModel === undefined ? {} : { model: voiceModel }),
      ...(voiceName === undefined ? {} : { name: voiceName }),
    };
  }
  const cast = draft.cast
    .filter((row) =>
      [row.character, row.voiceId, row.gender, row.reference, row.look].some((one) => one.trim() !== ''),
    )
    .map((row) => ({
      character: row.character.trim(),
      ...(row.gender === '' ? {} : { gender: row.gender }),
      ...(row.reference.trim() === '' ? {} : { reference: row.reference.trim() }),
      ...(row.look.trim() === '' ? {} : { look: row.look.trim().replace(/\s+/g, ' ') }),
      ...(row.provider === '' ? {} : { provider: row.provider }),
      ...(row.voiceId.trim() === '' ? {} : { voiceId: row.voiceId.trim() }),
      ...(row.model.trim() === '' ? {} : { model: row.model.trim() }),
      ...(row.name.trim() === '' ? {} : { name: row.name.trim() }),
      ...(row.language.trim() === '' ? {} : { language: row.language.trim() }),
      ...(row.accent.trim() === '' ? {} : { accent: row.accent.trim() }),
    }));
  if (cast.length > 0) profile.cast = cast;
  const rejectedVoices = draft.rejectedVoices
    .filter((row) => row.voiceId.trim() !== '' || row.reason.trim() !== '')
    .map((row) => ({
      voiceId: row.voiceId.trim(),
      ...(row.reason.trim() === '' ? {} : { reason: row.reason.trim() }),
    }));
  if (rejectedVoices.length > 0) profile.rejectedVoices = rejectedVoices;
  // A line typed here is the operator's own: it says so, and when.
  const typedAt = new Date().toISOString();
  const approvedLines = [
    ...draft.approvedLines,
    ...(lines(draft.newApprovedLines) ?? [])
      .filter((line) => !draft.approvedLines.some((one) => approvedText(one) === line))
      .map((line): ProfileApprovedLine => ({ text: line, source: 'operator', contentId: null, at: typedAt })),
  ];
  if (approvedLines.length > 0) profile.approvedLines = approvedLines;
  if (draft.removedApprovedLines.length > 0) profile.removedApprovedLines = draft.removedApprovedLines;
  const rejectedLines = lines(draft.rejectedLines);
  if (rejectedLines !== undefined) profile.rejectedLines = rejectedLines;
  const notes = text(draft.notes);
  if (notes !== undefined) profile.notes = notes;
  return profile;
}

/** The words of an approved line, tagged or not. */
function approvedText(line: string | ProfileApprovedLine): string {
  return typeof line === 'string' ? line : line.text;
}

/** Where an approved line comes from, as the list says it. */
function approvedOrigin(line: string | ProfileApprovedLine): string {
  if (typeof line === 'string') return 'no source yet';
  const when = line.at.slice(0, 10);
  return line.source === 'approved-video' ? `approved video · ${when}` : `typed by you · ${when}`;
}

/** How many of the fields say something, for the folded panel's one line. */
function filledFields(profile: ProductProfile): number {
  return Object.values(profile).filter((value) => value !== undefined).length;
}

/**
 * The profile editor on the brand book page: every field, one form, saved
 * whole. It starts folded unless asked otherwise — most visits to the
 * Repositories page are about keys and clones — and says in one line how much
 * of the profile is filled in.
 */
export function ProductProfilePanel({
  brandBookId,
  name,
  defaultOpen = false,
}: {
  readonly brandBookId: string;
  /** What the toast calls the book. */
  readonly name: string;
  readonly defaultOpen?: boolean;
}) {
  const toast = useToast();
  const [view, setView] = useState<BrandBookProfileView | null>(null);
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** The book's uploaded images, offered as a cast character's reference. */
  const [stills, setStills] = useState<ContextItem[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    fetchContextItems(brandBookId, controller.signal)
      .then((items) => setStills(items.filter((item) => item.type === 'image' && item.status === 'ready')))
      .catch(() => setStills([]));
    return () => controller.abort();
  }, [brandBookId]);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        const next = await fetchBrandBookProfile(brandBookId, signal);
        setView(next);
        setDraft(toDraft(next.profile));
        setError(null);
      } catch (cause: unknown) {
        if (signal?.aborted === true) return;
        if (redirectIfUnauthorised(cause)) return;
        setError(describeError(cause));
      }
    },
    [brandBookId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const dirty =
    view !== null && draft !== null && JSON.stringify(fromDraft(draft)) !== JSON.stringify(fromDraft(toDraft(view.profile)));

  const onSave = (): void => {
    if (draft === null) return;
    setSaving(true);
    saveBrandBookProfile(brandBookId, fromDraft(draft))
      .then((next) => {
        setView(next);
        setDraft(toDraft(next.profile));
        toast.ok(`Saved the product profile of ${name}.`);
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const filled = view === null ? 0 : filledFields(view.profile);
  const set = <K extends keyof ProfileDraft>(key: K, value: ProfileDraft[K]): void => {
    setDraft((current) => (current === null ? current : { ...current, [key]: value }));
  };

  return (
    <Panel
      id={`profile-${brandBookId}`}
      title="Product profile"
      icon="tag"
      collapsible
      defaultOpen={defaultOpen}
      meta={
        view === null ? null : filled === 0 ? (
          <Badge tone="neutral">empty</Badge>
        ) : (
          <Badge tone="done">{`${String(filled)} of ${String(Object.keys(FIELD_LABELS).length)} fields`}</Badge>
        )
      }
      summary="What every plan for this product starts from, so the planner never asks it again."
    >
      {error !== null && <Notice kind="error">{error}</Notice>}
      {draft === null ? (
        <Skeleton lines={5} />
      ) : (
        <form
          className="form form--tight"
          onSubmit={(event) => {
            event.preventDefault();
            onSave();
          }}
        >
          <p className="field__hint">
            What is true of this product on every content. The planner reads it before its first
            question and never asks what it states; it also proposes additions after each planning
            conversation, which you save from the content page. Leave anything you do not know
            empty.
          </p>

          <div className="field__row">
            <TextField
              id={`profile-language-${brandBookId}`}
              label={FIELD_LABELS.language}
              hint="A language tag such as nl-NL or en-US."
              value={draft.language}
              list={`profile-languages-${brandBookId}`}
              onChange={(value) => set('language', value)}
            />
            {isDutchLanguage(draft.language.trim() === '' ? 'x' : draft.language) && (
              <div className="field">
                <span className="field__label">{FIELD_LABELS.address}</span>
                <Segmented<ContentAddress | ''>
                  ariaLabel="Form of address"
                  value={draft.address}
                  options={[
                    { value: 'je', label: 'je' },
                    { value: 'u', label: 'u' },
                    { value: '', label: 'not set' },
                  ]}
                  onChange={(value) => set('address', value)}
                />
              </div>
            )}
            <TextField
              id={`profile-website-${brandBookId}`}
              label={FIELD_LABELS.website}
              hint="Where an ad sends the viewer, e.g. example.com."
              value={draft.website}
              onChange={(value) => set('website', value)}
            />
          </div>
          <datalist id={`profile-languages-${brandBookId}`}>
            {LANGUAGE_SUGGESTIONS.map((tag) => (
              <option key={tag} value={tag} />
            ))}
          </datalist>

          <div className="field__row">
            <TextField
              id={`profile-cta-label-${brandBookId}`}
              label="The ask: its words"
              hint="The product's own call to action, e.g. “Book a 30-minute demo”."
              value={draft.ctaLabel}
              onChange={(value) => set('ctaLabel', value)}
            />
            <TextField
              id={`profile-cta-url-${brandBookId}`}
              label="The ask: where it goes"
              value={draft.ctaUrl}
              onChange={(value) => set('ctaUrl', value)}
            />
          </div>

          <TextField
            id={`profile-descriptor-${brandBookId}`}
            label={FIELD_LABELS.descriptor}
            hint="The one way every ad introduces the product, name first, e.g. “Leo, de AI junior boekhouder”. The critic flags a script that introduces it otherwise."
            value={draft.descriptor}
            onChange={(value) => set('descriptor', value)}
          />

          <div className="field__row">
            <TextArea
              id={`profile-buyer-${brandBookId}`}
              label={FIELD_LABELS.buyer}
              hint="One role at one kind of firm."
              value={draft.buyer}
              onChange={(value) => set('buyer', value)}
            />
            <TextArea
              id={`profile-status-quo-${brandBookId}`}
              label={FIELD_LABELS.statusQuo}
              hint="The real competitor: a vacancy, a spreadsheet, evenings."
              value={draft.statusQuo}
              onChange={(value) => set('statusQuo', value)}
            />
          </div>

          <div className="field__row">
            <TextArea
              id={`profile-buyer-words-${brandBookId}`}
              label={FIELD_LABELS.buyerWords}
              hint="Phrases buyers use themselves, one a line."
              value={draft.buyerWords}
              onChange={(value) => set('buyerWords', value)}
            />
            <TextArea
              id={`profile-no-gos-${brandBookId}`}
              label={FIELD_LABELS.noGos}
              hint="What an ad must never imply or show, one a line."
              value={draft.noGos}
              onChange={(value) => set('noGos', value)}
            />
          </div>

          <div className="field__row">
            <TextArea
              id={`profile-allow-lines-${brandBookId}`}
              label={FIELD_LABELS.allowLines}
              hint="The product's fixed brand lines, one a line: they pass the banned list's absolutes rule wherever they are said."
              value={draft.allowLines}
              onChange={(value) => set('allowLines', value)}
            />
            <TextArea
              id={`profile-raise-to-fail-${brandBookId}`}
              label={FIELD_LABELS.raiseToFail}
              hint="Banned-list rule ids (e.g. absolutes) or words this product treats as a fail, one a line."
              value={draft.raiseToFail}
              onChange={(value) => set('raiseToFail', value)}
            />
          </div>

          <RowList
            label={FIELD_LABELS.competitors}
            hint="What a competitor says about itself, verbatim, with where and when you read it — one claim a row; rows with the same name are one competitor. Their words are never ours to say."
            rows={draft.competitors}
            empty={{ name: '', text: '', url: '', fetched: '' }}
            addLabel="Add a competitor claim"
            onChange={(rows) => set('competitors', rows)}
            render={(row, update) => (
              <>
                <input
                  className="field__input field__input--narrow"
                  type="text"
                  aria-label="Competitor"
                  placeholder="Competitor"
                  value={row.name}
                  onChange={(event) => update({ ...row, name: event.target.value })}
                />
                <input
                  className="field__input"
                  type="text"
                  aria-label="Their words"
                  placeholder="Their own words"
                  value={row.text}
                  onChange={(event) => update({ ...row, text: event.target.value })}
                />
                <input
                  className="field__input"
                  type="text"
                  aria-label="Where they say it"
                  placeholder="URL"
                  value={row.url}
                  onChange={(event) => update({ ...row, url: event.target.value })}
                />
                <input
                  className="field__input field__input--narrow"
                  type="date"
                  aria-label="Read on"
                  value={row.fetched}
                  onChange={(event) => update({ ...row, fetched: event.target.value })}
                />
              </>
            )}
          />

          <RowList
            label={FIELD_LABELS.objections}
            hint="Each fear a buyer has, and the product behaviour that answers it."
            rows={draft.objections}
            empty={{ objection: '', answer: '' }}
            addLabel="Add an objection"
            onChange={(rows) => set('objections', rows)}
            render={(row, update) => (
              <>
                <input
                  className="field__input"
                  type="text"
                  aria-label="Objection"
                  placeholder="The objection"
                  value={row.objection}
                  onChange={(event) => update({ ...row, objection: event.target.value })}
                />
                <input
                  className="field__input"
                  type="text"
                  aria-label="Answer"
                  placeholder="The answer"
                  value={row.answer}
                  onChange={(event) => update({ ...row, answer: event.target.value })}
                />
              </>
            )}
          />

          <RowList
            label={FIELD_LABELS.proof}
            hint="A claim an ad may make, with where it comes from — a claim without a source is refused. Tick “related party” when the source is the founder's own firm, an investor or a partner."
            rows={draft.proof}
            empty={{ claim: '', source: '', scope: '', relatedParty: false, permission: '', guarantee: false }}
            addLabel="Add proof"
            onChange={(rows) => set('proof', rows)}
            render={(row, update) => (
              <>
                <input
                  className="field__input"
                  type="text"
                  aria-label="Claim"
                  placeholder="The claim, exactly as it may be shown"
                  value={row.claim}
                  onChange={(event) => update({ ...row, claim: event.target.value })}
                />
                <input
                  className="field__input"
                  type="text"
                  aria-label="Source"
                  placeholder="Source"
                  value={row.source}
                  onChange={(event) => update({ ...row, source: event.target.value })}
                />
                <input
                  className="field__input"
                  type="text"
                  aria-label="Scope"
                  placeholder="What it is true of"
                  value={row.scope}
                  onChange={(event) => update({ ...row, scope: event.target.value })}
                />
                <input
                  className="field__input"
                  type="text"
                  aria-label="Permission"
                  placeholder="Permission, e.g. public"
                  value={row.permission}
                  onChange={(event) => update({ ...row, permission: event.target.value })}
                />
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={row.relatedParty}
                    onChange={(event) => update({ ...row, relatedParty: event.target.checked })}
                  />
                  related party
                </label>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={row.guarantee}
                    onChange={(event) => update({ ...row, guarantee: event.target.checked })}
                  />
                  guarantee
                </label>
              </>
            )}
          />

          <RowList
            label={FIELD_LABELS.pronunciations}
            hint="How the product's words are said: the written form, as captions show it, and the spoken form the voice is handed. The planner proposes these as it meets a brand name, an address or an acronym; the script always keeps the written form."
            rows={draft.pronunciations}
            empty={{ display: '', spoken: '', note: '' }}
            addLabel="Add a pronunciation"
            onChange={(rows) => set('pronunciations', rows)}
            render={(row, update) => (
              <>
                <input
                  className="field__input"
                  type="text"
                  aria-label="Written"
                  placeholder="Written, e.g. example.com"
                  value={row.display}
                  onChange={(event) => update({ ...row, display: event.target.value })}
                />
                <span aria-hidden="true">→</span>
                <input
                  className="field__input"
                  type="text"
                  aria-label="Spoken"
                  placeholder="Spoken, e.g. example dot com"
                  value={row.spoken}
                  onChange={(event) => update({ ...row, spoken: event.target.value })}
                />
                <input
                  className="field__input"
                  type="text"
                  aria-label="Note"
                  placeholder="Note"
                  value={row.note}
                  onChange={(event) => update({ ...row, note: event.target.value })}
                />
              </>
            )}
          />

          <div className="field">
            <span className="field__label">{FIELD_LABELS.voice}</span>
            <div className="field__pair">
              <select
                className="field__input field__input--narrow"
                aria-label="Voice provider"
                value={draft.voiceProvider}
                onChange={(event) => set('voiceProvider', event.target.value as TtsProvider | '')}
              >
                <option value="">Provider</option>
                <option value="elevenlabs">ElevenLabs</option>
                <option value="kokoro">Kokoro</option>
              </select>
              <input
                className="field__input"
                type="text"
                aria-label="Voice id"
                placeholder="Voice id"
                value={draft.voiceId}
                onChange={(event) => set('voiceId', event.target.value)}
              />
              <input
                className="field__input"
                type="text"
                aria-label="Voice model"
                placeholder="Model, e.g. eleven_multilingual_v2"
                value={draft.voiceModel}
                onChange={(event) => set('voiceModel', event.target.value)}
              />
              <input
                className="field__input"
                type="text"
                aria-label="Voice name"
                placeholder="What you call it"
                value={draft.voiceName}
                onChange={(event) => set('voiceName', event.target.value)}
              />
            </div>
            <p className="field__hint">One voice for every ad of this product; it wins over Settings.</p>
          </div>

          <RowList
            label={FIELD_LABELS.cast}
            hint="One face, one gender and one voice per recurring character, so they look and sound the same in every ad. Every still of a character is made from their reference (a library item, or an image uploaded under Context), and a voice labelled with the other gender is refused. A character with no voice here gets one saved automatically — the voice of their first take that passes the take check."
            rows={draft.cast}
            empty={{
              character: '',
              gender: '' as CastGender | '',
              reference: '',
              look: '',
              provider: '' as TtsProvider | '',
              voiceId: '',
              model: '',
              name: '',
              language: '',
              accent: '',
            }}
            addLabel="Add a character"
            onChange={(rows) => set('cast', rows)}
            render={(row, update) => (
              <>
                <input
                  className="field__input"
                  type="text"
                  aria-label="Character"
                  placeholder="Character, e.g. Leo"
                  value={row.character}
                  onChange={(event) => update({ ...row, character: event.target.value })}
                />
                <select
                  className="field__input field__input--narrow"
                  aria-label="Character gender"
                  value={row.gender}
                  onChange={(event) => update({ ...row, gender: event.target.value as CastGender | '' })}
                >
                  <option value="">Gender</option>
                  <option value="f">f — female voice</option>
                  <option value="m">m — male voice</option>
                  <option value="x">x — either voice</option>
                </select>
                <input
                  className="field__input"
                  type="text"
                  aria-label="Character reference"
                  placeholder="Reference: library:<id> or an uploaded still"
                  list={`cast-stills-${brandBookId}`}
                  value={row.reference}
                  onChange={(event) => update({ ...row, reference: event.target.value })}
                />
                <datalist id={`cast-stills-${brandBookId}`}>
                  {stills.map((item) => (
                    <option key={item.id} value={`context:${item.id}`}>
                      {item.title}
                    </option>
                  ))}
                </datalist>
                <input
                  className="field__input"
                  type="text"
                  aria-label="Character look"
                  placeholder="Look, in one line: mid-30s, short dark hair, green cardigan"
                  value={row.look}
                  onChange={(event) => update({ ...row, look: event.target.value })}
                />
                <select
                  className="field__input field__input--narrow"
                  aria-label="Cast voice provider"
                  value={row.provider}
                  onChange={(event) => update({ ...row, provider: event.target.value as TtsProvider | '' })}
                >
                  <option value="">Provider</option>
                  <option value="elevenlabs">ElevenLabs</option>
                  <option value="kokoro">Kokoro</option>
                </select>
                <input
                  className="field__input"
                  type="text"
                  aria-label="Cast voice id"
                  placeholder="Voice id"
                  value={row.voiceId}
                  onChange={(event) => update({ ...row, voiceId: event.target.value })}
                />
                <input
                  className="field__input"
                  type="text"
                  aria-label="Cast voice model"
                  placeholder="Model"
                  value={row.model}
                  onChange={(event) => update({ ...row, model: event.target.value })}
                />
                <input
                  className="field__input"
                  type="text"
                  aria-label="Cast voice name"
                  placeholder="Voice name"
                  value={row.name}
                  onChange={(event) => update({ ...row, name: event.target.value })}
                />
                <input
                  className="field__input field__input--narrow"
                  type="text"
                  aria-label="Cast language"
                  placeholder="nl-NL"
                  value={row.language}
                  onChange={(event) => update({ ...row, language: event.target.value })}
                />
                <input
                  className="field__input"
                  type="text"
                  aria-label="Accent"
                  placeholder="Accent, e.g. standard Dutch"
                  value={row.accent}
                  onChange={(event) => update({ ...row, accent: event.target.value })}
                />
              </>
            )}
          />

          <RowList
            label={FIELD_LABELS.rejectedVoices}
            hint="Voices this brand never uses again, each with why. A take asked for with one of them is refused, and none is ever cast."
            rows={draft.rejectedVoices}
            empty={{ voiceId: '', reason: '' }}
            addLabel="Add a rejected voice"
            onChange={(rows) => set('rejectedVoices', rows)}
            render={(row, update) => (
              <>
                <input
                  className="field__input"
                  type="text"
                  aria-label="Rejected voice id"
                  placeholder="Voice id"
                  value={row.voiceId}
                  onChange={(event) => update({ ...row, voiceId: event.target.value })}
                />
                <input
                  className="field__input"
                  type="text"
                  aria-label="Reason"
                  placeholder="Why, e.g. sounds Belgian"
                  value={row.reason}
                  onChange={(event) => update({ ...row, reason: event.target.value })}
                />
              </>
            )}
          />

          <div className="field">
            <span className="field__label">{FIELD_LABELS.approvedLines}</span>
            {draft.approvedLines.length === 0 ? (
              <p className="field__hint">None yet.</p>
            ) : (
              <ul className="profile-lines" aria-label="Approved lines">
                {draft.approvedLines.map((line) => (
                  <li key={approvedText(line)} className="profile-lines__row">
                    <span className="profile-lines__text">“{approvedText(line)}”</span>
                    <span className="field__hint">{approvedOrigin(line)}</span>
                    <button
                      type="button"
                      className="button button--small button--quiet"
                      aria-label={`Remove “${approvedText(line)}”`}
                      onClick={() =>
                        set(
                          'approvedLines',
                          draft.approvedLines.filter((one) => approvedText(one) !== approvedText(line)),
                        )
                      }
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="field__hint">
              The house voice the planner writes toward: the script of every video you approve is added on its own, and
              a line you type below is saved as yours. Planning never adds a line here.
            </p>
          </div>

          {draft.removedApprovedLines.length > 0 && (
            <div className="field">
              <span className="field__label">{FIELD_LABELS.removedApprovedLines}</span>
              <p className="field__hint">
                Old approved lines found in no video you approved and in nothing you typed — most likely the planner’s own
                words. Restore one to keep it as yours, then save.
              </p>
              <ul className="profile-lines" aria-label="Removed by migration">
                {draft.removedApprovedLines.map((line) => (
                  <li key={line.text} className="profile-lines__row">
                    <span className="profile-lines__text">“{line.text}”</span>
                    <span className="field__hint">{`removed ${line.removedAt.slice(0, 10)}`}</span>
                    <button
                      type="button"
                      className="button button--small"
                      onClick={() =>
                        setDraft({
                          ...draft,
                          approvedLines: [
                            ...draft.approvedLines,
                            { text: line.text, source: 'operator', contentId: null, at: new Date().toISOString() },
                          ],
                          removedApprovedLines: draft.removedApprovedLines.filter((one) => one.text !== line.text),
                        })
                      }
                    >
                      Restore
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="field__row">
            <TextArea
              id={`profile-approved-${brandBookId}`}
              label="Add approved lines"
              hint="Lines you approve, one a line: saved as typed by you."
              value={draft.newApprovedLines}
              onChange={(value) => set('newApprovedLines', value)}
            />
            <TextArea
              id={`profile-rejected-${brandBookId}`}
              label={FIELD_LABELS.rejectedLines}
              hint="Lines you turned down, one a line: what not to write again."
              value={draft.rejectedLines}
              onChange={(value) => set('rejectedLines', value)}
            />
          </div>

          <TextArea
            id={`profile-notes-${brandBookId}`}
            label={FIELD_LABELS.notes}
            value={draft.notes}
            onChange={(value) => set('notes', value)}
          />

          <div className="field__actions">
            <button type="submit" className="button button--primary button--small" disabled={!dirty || saving}>
              <Icon name={saving ? 'sync' : 'check'} />
              {saving ? 'Saving…' : 'Save profile'}
            </button>
            {dirty && (
              <button
                type="button"
                className="button button--small button--quiet"
                disabled={saving}
                onClick={() => {
                  if (view !== null) setDraft(toDraft(view.profile));
                }}
              >
                Discard changes
              </button>
            )}
            {view !== null && filled > 0 && (
              <span className="field__hint">Last saved {localTime(view.updatedAt)}.</span>
            )}
          </div>
        </form>
      )}
    </Panel>
  );
}

function TextField({
  id,
  label,
  hint,
  value,
  list,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly value: string;
  readonly list?: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="field__input"
        type="text"
        value={value}
        list={list}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint !== undefined && <p className="field__hint">{hint}</p>}
    </div>
  );
}

function TextArea({
  id,
  label,
  hint,
  value,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        className="field__input"
        rows={3}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint !== undefined && <p className="field__hint">{hint}</p>}
    </div>
  );
}

/** A list of small rows, each with its own remove button, and one add button. */
export function RowList<T>({
  label,
  hint,
  rows,
  empty,
  addLabel,
  onChange,
  render,
}: {
  readonly label: string;
  readonly hint: string;
  readonly rows: readonly T[];
  readonly empty: T;
  readonly addLabel: string;
  readonly onChange: (rows: T[]) => void;
  readonly render: (row: T, update: (row: T) => void) => ReactNode;
}) {
  return (
    <div className="field">
      <span className="field__label">{label}</span>
      <p className="field__hint">{hint}</p>
      {rows.map((row, index) => (
        <div className="field__pair" key={index}>
          {render(row, (next) => onChange(rows.map((one, at) => (at === index ? next : one))))}
          <button
            type="button"
            className="button button--small button--quiet button--danger button--icon"
            aria-label={`Remove this entry from ${label.toLowerCase()}`}
            title="Remove"
            onClick={() => onChange(rows.filter((_, at) => at !== index))}
          >
            <Icon name="trash" />
          </button>
        </div>
      ))}
      <div className="field__actions">
        <button type="button" className="button button--small" onClick={() => onChange([...rows, empty])}>
          <Icon name="plus" />
          {addLabel}
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- proposal */

/**
 * The product facts the planner learned, on the content page. While planning
 * runs they are "Product facts learned while planning", shown beside what the
 * product already knows; when planning ends the server saves them to the brand
 * book on its own (US-014) and the panel becomes "Saved automatically", with
 * what was saved, what the profile refused, and **Undo**. It renders nothing
 * until there is either, and reads again whenever `watch` changes — the
 * planning panel's state, so it follows the conversation and Mark ready.
 */
export function ProfileProposalPanel({
  contentId,
  watch,
}: {
  readonly contentId: string;
  readonly watch: string;
}) {
  const toast = useToast();
  const [proposal, setProposal] = useState<ProfileProposal | null>(null);
  const [busy, setBusy] = useState<'undo' | 'dismiss' | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchProfileProposal(contentId, controller.signal)
      .then(setProposal)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        redirectIfUnauthorised(cause);
      });
    return () => controller.abort();
  }, [contentId, watch]);

  if (proposal === null) return null;
  if (proposal.exists) return <PendingFacts proposal={proposal} busy={busy} onDismiss={onDismiss} />;
  if (proposal.autoSaved !== null) {
    return <SavedFacts saved={proposal.autoSaved} busy={busy} onUndo={onUndo} />;
  }
  return null;

  function onUndo(): void {
    setBusy('undo');
    undoProfileAutoSave(contentId)
      .then((result) => {
        setProposal(result.proposal);
        toast.ok(`Put the product profile of ${result.saved.brandBookName} back as it was.`);
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(null);
      });
  }

  function onDismiss(): void {
    setBusy('dismiss');
    dismissProfileProposal(contentId)
      .then((next) => {
        setProposal(next);
        toast.ok('Set the proposed product facts aside.');
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(null);
      });
  }
}

/** While planning runs: what the planner wrote so far, saved when planning ends. */
function PendingFacts({
  proposal,
  busy,
  onDismiss,
}: {
  readonly proposal: ProfileProposal;
  readonly busy: 'undo' | 'dismiss' | null;
  readonly onDismiss: () => void;
}) {
  const changes = proposal.fields.filter((one) => one.change !== 'same');
  const known = proposal.fields.filter((one) => one.change === 'same');
  return (
    <Panel
      id="product-facts"
      title="Product facts learned while planning"
      icon="tag"
      meta={
        changes.length === 0 ? (
          <Badge tone="neutral">nothing new</Badge>
        ) : (
          <Badge tone="active">{`${String(changes.length)} to save`}</Badge>
        )
      }
    >
      <div className="stack">
        <p className="field__hint">
          The planner wrote down what it confirmed or learned about the product
          {proposal.proposedAt === null ? '' : ` (${localTime(proposal.proposedAt)})`}.
          {proposal.brandBookId !== null && proposal.brandBookName !== null && (
            <>
              {' '}They are saved to brand book <BookLink id={proposal.brandBookId} name={proposal.brandBookName} />{' '}
              when planning ends, and the next content in that book starts from them instead of asking
              again.
            </>
          )}
        </p>

        {proposal.brandBookId === null && (
          <Notice kind="warn">
            This content has no brand book, so there is no product profile to save the facts to.
            Dismiss them, or add them to a brand book’s profile by hand.
          </Notice>
        )}

        <Problems problems={proposal.problems} intro="Some of what the planner wrote could not be read and is left out:" />

        {changes.length > 0 && (
          <div className="rows rows--divided">
            {changes.map((one) => (
              <ProposalRow key={one.field} field={one} before="Now" />
            ))}
          </div>
        )}

        {known.length > 0 && (
          <p className="field__hint">
            Already in the profile, unchanged: {known.map((one) => FIELD_LABELS[one.field]).join(', ')}.
          </p>
        )}

        <div className="field__actions">
          <button type="button" className="button button--small button--quiet" disabled={busy !== null} onClick={onDismiss}>
            {busy === 'dismiss' ? 'Dismissing…' : 'Dismiss'}
          </button>
        </div>
      </div>
    </Panel>
  );
}

/** After planning ended: what was saved on its own, and **Undo**. */
function SavedFacts({
  saved,
  busy,
  onUndo,
}: {
  readonly saved: ProfileAutoSave;
  readonly busy: 'undo' | 'dismiss' | null;
  readonly onUndo: () => void;
}) {
  const undone = saved.undoneAt !== null;
  return (
    <Panel
      id="product-facts"
      title="Saved automatically"
      icon="tag"
      meta={
        undone ? (
          <Badge tone="neutral">undone</Badge>
        ) : saved.fields.length === 0 ? (
          <Badge tone="neutral">nothing new</Badge>
        ) : (
          <Badge tone="done">{`${String(saved.fields.length)} saved`}</Badge>
        )
      }
    >
      <div className="stack">
        <p className="field__hint">
          When planning ended ({localTime(saved.savedAt)}), what the planner learned about the product was saved to
          brand book{' '}
          {saved.brandBookName === null ? (
            'that has since been deleted'
          ) : (
            <BookLink id={saved.brandBookId} name={saved.brandBookName} />
          )}
          , so the next content in that book starts from it.
          {undone && ` Undone ${localTime(saved.undoneAt ?? '')}: the profile is back as it was before.`}
        </p>

        <Problems
          problems={saved.skipped}
          intro="The profile refused some of what the planner wrote, so it was skipped:"
        />

        {saved.fields.length > 0 && (
          <div className="rows rows--divided">
            {saved.fields.map((one) => (
              <ProposalRow key={one.field} field={one} before="Before" />
            ))}
          </div>
        )}

        {saved.unchanged.length > 0 && (
          <p className="field__hint">
            Already in the profile, unchanged: {saved.unchanged.map((one) => FIELD_LABELS[one]).join(', ')}.
          </p>
        )}

        {!undone && saved.fields.length > 0 && (
          <div className="field__actions">
            <button
              type="button"
              className="button button--small"
              disabled={busy !== null || saved.undoBlocked !== null}
              title={saved.undoBlocked ?? undefined}
              onClick={onUndo}
            >
              <Icon name={busy === 'undo' ? 'sync' : 'history'} />
              {busy === 'undo' ? 'Undoing…' : 'Undo'}
            </button>
            {saved.undoBlocked !== null && <span className="field__hint">{saved.undoBlocked}</span>}
          </div>
        )}
      </div>
    </Panel>
  );
}

function BookLink({ id, name }: { readonly id: string; readonly name: string }) {
  return (
    <Link className="link" href={brandBookPath(id)}>
      <strong>{name}</strong>
    </Link>
  );
}

function Problems({
  problems,
  intro,
}: {
  readonly problems: readonly { field: string; message: string }[];
  readonly intro: string;
}) {
  if (problems.length === 0) return null;
  return (
    <Notice kind="warn">
      {intro}
      <ul>
        {problems.map((problem) => (
          <li key={`${problem.field}:${problem.message}`}>
            <span className="mono">{problem.field}</span>: {problem.message}
          </li>
        ))}
      </ul>
    </Notice>
  );
}

function ProposalRow({ field, before }: { readonly field: ProposalField; readonly before: string }) {
  const list = Array.isArray(field.proposed);
  return (
    <div className="row row--stacked">
      <div>
        <strong>{FIELD_LABELS[field.field]}</strong>{' '}
        <Badge tone={field.change === 'new' ? 'done' : 'active'}>{field.change}</Badge>
      </div>
      <div className="facts">
        {field.current !== null && (
          <div className="facts__row">
            <span className="muted">{before}</span> <span>{summarise(field.field, field.current)}</span>
          </div>
        )}
        <div className="facts__row">
          <span className="muted">{list && field.current !== null ? 'Adds' : 'Proposed'}</span>{' '}
          <span>{summarise(field.field, list ? field.additions : field.proposed)}</span>
        </div>
      </div>
    </div>
  );
}

/** One field's value as one readable line. */
function summarise(field: ProductProfileField, value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.map((entry) => entryText(field, entry)).join(' · ');
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (field === 'cta') return [quoted(record['label']), arrow(record['url'])].filter((part) => part !== '').join(' ');
    if (field === 'voice') {
      const details = [record['provider'], record['voiceId'], record['model']].filter(
        (part): part is string => typeof part === 'string',
      );
      return `${typeof record['name'] === 'string' ? record['name'] : 'unnamed'}${details.length === 0 ? '' : ` (${details.join(', ')})`}`;
    }
    return JSON.stringify(value);
  }
  return String(value);
}

function entryText(field: ProductProfileField, entry: unknown): string {
  if (typeof entry === 'string') return `“${entry}”`;
  if (entry === null || typeof entry !== 'object') return String(entry);
  const record = entry as Record<string, unknown>;
  if (field === 'objections') {
    return `${String(record['objection'])}${typeof record['answer'] === 'string' ? ` → ${record['answer']}` : ''}`;
  }
  if (field === 'proof') {
    return `“${String(record['claim'])}” (${String(record['source'])}${record['relatedParty'] === true ? ', related party' : ''})`;
  }
  if (field === 'pronunciations') return `${String(record['display'])} → “${String(record['spoken'])}”`;
  if (field === 'approvedLines' || field === 'removedApprovedLines') return `“${String(record['text'])}”`;
  if (field === 'competitors') {
    const claims = Array.isArray(record['claims']) ? (record['claims'] as { text?: unknown }[]) : [];
    return `${String(record['name'])}${claims.length === 0 ? '' : `: ${claims.map((one) => `“${String(one.text)}”`).join(', ')}`}`;
  }
  if (field === 'cast') {
    const voice =
      typeof record['voiceId'] !== 'string'
        ? 'no voice yet'
        : typeof record['name'] === 'string'
          ? `${record['name']} (${record['voiceId']})`
          : record['voiceId'];
    const face = [record['gender'], record['reference'], record['look']].filter((one) => typeof one === 'string');
    return `${String(record['character'])}: ${voice}${face.length === 0 ? '' : ` — ${face.join(', ')}`}`;
  }
  if (field === 'rejectedVoices') {
    return `${String(record['voiceId'])}${typeof record['reason'] === 'string' ? ` — ${record['reason']}` : ''}`;
  }
  return JSON.stringify(entry);
}

function quoted(value: unknown): string {
  return typeof value === 'string' ? `“${value}”` : '';
}

function arrow(value: unknown): string {
  return typeof value === 'string' ? `→ ${value}` : '';
}
